import React from "react";

import { t } from "i18next";
import { useFormContext } from "react-hook-form";

import { Field, FieldError, FieldLabel } from "@/src/components/ui/field";
import { ToggleGroup, ToggleGroupItem } from "@/src/components/ui/toggle-group";

import type { JSX } from "react";

import type { ProviderPlan } from "@/src/api/OfferAPI.types";
import type { PlanFormValues } from "@/src/components/offers/offerPlanAdapter";

/** Chip-shaped toggle: rounded pill with a primary highlight when selected. */
const TOGGLE_CHIP_ITEM_CLASS =
  "text-muted-foreground rounded-full px-4 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:hover:bg-primary/10 data-[state=on]:hover:text-primary";

interface PlanPaymentFieldProps {
  availableProviderPlanList: ProviderPlan[];
  activeProviderPlanList: ProviderPlan[];
}

const PROVIDER_LIST: { value: ProviderPlan; label: string }[] = [
  { value: "stripe", label: "Stripe" },
  { value: "paypal", label: "PayPal" },
];

const isProviderPlan = (value: string): value is ProviderPlan =>
  PROVIDER_LIST.some((provider) => provider.value === value);

function PlanPaymentField({
  availableProviderPlanList,
  activeProviderPlanList,
}: PlanPaymentFieldProps): JSX.Element {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PlanFormValues>();
  const value = watch("provider_plans") ?? [];

  // Only render the providers actually configured on the project.
  const configuredProviderList = PROVIDER_LIST.filter((provider) =>
    availableProviderPlanList.includes(provider.value),
  );
  // A single configured provider is mandatory on an okast plan (the only
  // platform this field renders under) — its toggle is locked on. The forced
  // selection itself is applied in usePlanFormDerivations.
  const isSingleProvider = configuredProviderList.length === 1;

  return (
    <Field>
      <FieldLabel>{t("editor-offers:paymentMethods")}</FieldLabel>
      <ToggleGroup
        spacing={2}
        type="multiple"
        value={value}
        variant="outline"
        onValueChange={(next: string[]) =>
          setValue("provider_plans", next.filter(isProviderPlan), {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      >
        {configuredProviderList.map((provider) => {
          // Providers already persisted on the plan can't be removed either.
          const isLocked =
            isSingleProvider || activeProviderPlanList.includes(provider.value);
          return (
            <ToggleGroupItem
              key={provider.value}
              className={TOGGLE_CHIP_ITEM_CLASS}
              disabled={isLocked}
              value={provider.value}
            >
              {provider.label}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
      <FieldError>{errors.provider_plans?.message}</FieldError>
    </Field>
  );
}

export default PlanPaymentField;
